import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import * as labels from "config.labels";
import { parseAddressComponents } from "domain/address";
import { INSURED_ADDRESS_AUTO_PARSE } from "flags";
import { useEffect, useRef } from "react";
import { useFeatureFlag } from "store/hooks/featureFlagHooks";
import { useTranslator } from "store/hooks/staticDataHooks";

// `parseAddressComponents` now lives in `domain/address` alongside `composeAddress`
// (shared with the people/broker editor). Re-exported here for existing importers.
export { parseAddressComponents };

const InsuredAddressComponents = ({
  insuredAddress,
  insuredAddressComponents,
  updateInsuredAddressComponents,
}) => {
  const { isOn: autoParse } = useFeatureFlag(INSURED_ADDRESS_AUTO_PARSE);
  const translateLabel = useTranslator("uiLabels");
  const components = insuredAddressComponents || {};
  const prevAddressRef = useRef(insuredAddress);
  const updateRef = useRef(updateInsuredAddressComponents);
  updateRef.current = updateInsuredAddressComponents;

  useEffect(() => {
    if (!autoParse) return;
    if (insuredAddress === prevAddressRef.current) return;
    prevAddressRef.current = insuredAddress;
    updateRef.current(parseAddressComponents(insuredAddress));
  }, [insuredAddress, autoParse]);

  const update = (field) => (e) =>
    updateInsuredAddressComponents({ ...components, [field]: e.target.value });

  return (
    <>
      <Grid item xs={12}>
        <TextField
          label={translateLabel(labels.INSURED_ADDRESS_STREET_LABEL, {
            default: "Address Line 1",
          })}
          size={"small"}
          fullWidth
          value={components.street ?? ""}
          onChange={update("street")}
          variant={"filled"}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label={translateLabel(labels.INSURED_ADDRESS_STREET2_LABEL, {
            default: "Address Line 2",
          })}
          size={"small"}
          fullWidth
          value={components.street2 ?? ""}
          onChange={update("street2")}
          variant={"filled"}
        />
      </Grid>
      <Grid item xs={12} sm={5}>
        <TextField
          label={translateLabel(labels.INSURED_ADDRESS_CITY_LABEL, {
            default: "City",
          })}
          size={"small"}
          fullWidth
          value={components.city ?? ""}
          onChange={update("city")}
          variant={"filled"}
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <TextField
          label={translateLabel(labels.INSURED_ADDRESS_STATE_LABEL, {
            default: "State",
          })}
          size={"small"}
          fullWidth
          value={components.state ?? ""}
          onChange={update("state")}
          variant={"filled"}
        />
      </Grid>
      <Grid item xs={6} sm={4}>
        <TextField
          label={translateLabel(labels.INSURED_ADDRESS_ZIP_LABEL, {
            default: "Zip Code",
          })}
          size={"small"}
          fullWidth
          value={components.zip ?? ""}
          onChange={update("zip")}
          variant={"filled"}
        />
      </Grid>
    </>
  );
};

export default InsuredAddressComponents;
